import * as ko from "knockout";

import { UnmappableLocation, ErrorLevel } from "./api";


function getUniqueNumber(): number {
    return new Date().getTime() + Math.floor(Math.random() * 1000);
}


/**
 * A view model for displaying a map progress indicator error tooltip.
 */
export class MapProgressTooltip {
    /**
     * Creates a new instance of the MapProgressTooltip class.
     */
    constructor() {
        this.id = getUniqueNumber().toString();
        this.unmappableLocations = ko.observableArray([]);
        this.visible = ko.observable(false);
        this._disableMouseOutHiding = ko.observable(false);
        this._enabled = ko.observable(true);
        this.title = ko.pureComputed(() => this._computeTitle());

        this.visible.subscribe((isVisible: boolean) => {
            if (!isVisible) {
                this.enableMouseOutHiding();
            }
        });
    }


    /**
     * Sorts and loads the given unmappable locations into the tooltip.
     * @param {UnmappableLocation[]} record The locations to load.
     */
    loadViewModel(record: UnmappableLocation[]): void {
        if (record) {
            record.sort((a: UnmappableLocation, b: UnmappableLocation) => {
                // Sort errors before warnings
                if (a.errorLevel === ErrorLevel.Error && b.errorLevel !== ErrorLevel.Error) {
                    return -1;
                }
                if (a.errorLevel !== ErrorLevel.Error && b.errorLevel === ErrorLevel.Error) {
                    return 1;
                }
                return a.titleDisplayName > b.titleDisplayName ? 1 : a.titleDisplayName < b.titleDisplayName ? -1 : 0;
            });
            this.unmappableLocations(record);
        }
    }


    /**
     * Called when the mouse leaves the tooltip.
     */
    mouseOut(): boolean {
        if (this._disableMouseOutHiding()) {
            return false;
        }
        return this.hide();
    }


    /**
     * Shows the tooltip.
     */
    show(): boolean {
        if (!this._enabled()) {
            return false;
        }
        this.visible(true);
        return false;
    }


    /**
     * Hides the tooltip.
     */
    hide(): boolean {
        if (!this._enabled()) {
            return false;
        }
        this.visible(false);
        return true; // allow event propagation & default handling
    }


    /**
     * Hides the MapProgressTooltip and navigates to an unmappable location.
     * @param {UnmappableLocation} location The unmappable location to navigate to.
     */
    openItem(location: UnmappableLocation): boolean {
        this.hide();
        location.openItem();

        return false;
    }


    /**
     * Enables this tooltip.
     */
    enable(): void {
        this._enabled(true);
    }


    /**
     * Disables this tooltip.
     */
    disable(): void {
        this._enabled(false);
        this.visible(false);
    }


    /**
     * Enables hiding this tooltip when the mouse leaves it.
     */
    enableMouseOutHiding(): void {
        this._disableMouseOutHiding(false);
    }


    /**
     * Disables hiding this tooltip when the mouse leaves it.
     */
    disableMouseOutHiding(): void {
        this._disableMouseOutHiding(true);
    }


    /**
     * Get the css class for a given tooltip location item.
     * @param  {UnmappableLocation} location
     *         The item to get the css class for.
     * @return {string}
     *         The css class for the item.
     */
    getItemCss(location: UnmappableLocation): string {
        switch (location.errorLevel) {
            case ErrorLevel.Error:
                return "error";
            default: // warning
                return "warning";
        }
    }


    private _computeTitle(): string {
        const unmappableLocations = this.unmappableLocations() || [];
        const hasWarnings = unmappableLocations.some((location: UnmappableLocation) => {
            return location.errorLevel === ErrorLevel.Warning;
        });

        if (hasWarnings) {
            const hasErrors = unmappableLocations.some((location: UnmappableLocation) => {
                return location.errorLevel === ErrorLevel.Error;
            });

            if (hasErrors) {
                return "Errors & Warnings";
            }

            return "Warnings";
        }

        return "Errors";
    }


    /**
     * The id for this tooltip.
     */
    id: string;
    /**
     * The title to display for this tooltip.
     */
    title: ko.Computed<string>;
    /**
     * The unmappable locations to display in the tooltip.
     */
    unmappableLocations: ko.ObservableArray<UnmappableLocation>;
    /**
     * true is this tooltip is visible, false otherwise.
     */
    visible: ko.Observable<boolean>;


    private _disableMouseOutHiding: ko.Observable<boolean>;
    private _enabled: ko.Observable<boolean>;
}



// WEBPACK FOOTER //
// mapProgressTooltip.ts